/**
 * Module dependencies.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { exportsApi } from 'app/core/api/exports-api';

/**
 * Export `ExportsCountContext` interface.
 */

interface ExportsCountContextValue {
  count: number;
  refetch: () => Promise<void>;
  isLoading: boolean;
}

/**
 * Export `ExportsCountContext`.
 */

const ExportsCountContext = createContext<ExportsCountContextValue | undefined>(undefined);

/**
 * Export `ExportsCountProvider` component.
 */

export function ExportsCountProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const client = useRequestProvider();

  const fetchCount = useCallback(async () => {
    setIsLoading(true);
    try {
      const unseenCount = await exportsApi.getUnseenCount(client);
      setCount(unseenCount);
    } catch (error) {
      console.error('Failed to fetch exports count:', error);
    } finally {
      setIsLoading(false);
    }
  }, [client]);

  // Fetch on mount and periodically
  useEffect(() => {
    fetchCount();
    const interval = setInterval(() => {
      fetchCount();
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [fetchCount]);

  const value: ExportsCountContextValue = {
    count,
    refetch: fetchCount,
    isLoading
  };

  return <ExportsCountContext.Provider value={value}>{children}</ExportsCountContext.Provider>;
}

/**
 * Export `useExportsCount` hook.
 */

export function useExportsCount(): ExportsCountContextValue {
  const context = useContext(ExportsCountContext);

  if (!context) {
    throw new Error('useExportsCount must be used within ExportsCountProvider');
  }

  return context;
}
